import { useEffect, useState } from 'react'
import { fetchIsMultimarket } from '../data/rest/apiMigration'
import { useProjectType } from '../modules/Project/Project.hooks'

type UseSurveyCategoricalVersionResult = {
  isLoading: boolean
  // null = loading/unknown, true = use new API, false = use legacy API
  useNewApi: boolean | null
}

/**
 * Decides whether a survey should hit the new analytics API or the legacy one.
 *
 * - Non-multimarket surveys: always use the new API. The new backend rehydrates
 *   missing data on demand (returns 202 + Retry-After), so there is no need to
 *   fall back to legacy when the survey hasn't been ingested yet.
 * - Legacy (v1) multimarket surveys: a single survey holds every market and
 *   rehydration doesn't apply, so we still need the pre-existing routing — call
 *   /v1/core-responses/multimarket/.../is-multimarket and only use the new API
 *   if the multimarket service has the survey.
 * - MultiMarketV2 markets: each market is an ordinary per-market survey living
 *   in core-responses, so they behave like non-multimarket surveys and go
 *   straight to the new API (rehydration handles not-yet-ingested data).
 */
export const useSurveyCategoricalVersion = (
  surveyId: string
): UseSurveyCategoricalVersionResult => {
  // Only legacy (v1) multimarket surveys (isMultiMarket) need the async
  // is-multimarket routing check. MultiMarketV2 markets are ordinary per-market
  // surveys, so — like non-multimarket surveys — they use the new API directly.
  const { isMultiMarket, loading: projectLoading } = useProjectType()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [multimarketInCoreResponses, setMultimarketInCoreResponses] = useState<
    boolean | null
  >(null)

  useEffect(() => {
    if (projectLoading) {
      return
    }

    if (!surveyId) {
      setIsLoading(false)
      return
    }

    if (!isMultiMarket) {
      // Non-MM (and MultiMarketV2) surveys go straight to the new API; no async
      // check needed.
      setIsLoading(false)
      return
    }

    let cancelled = false
    const checkMultimarket = async () => {
      setIsLoading(true)
      try {
        const response = await fetchIsMultimarket(surveyId)
        if (!cancelled) setMultimarketInCoreResponses(response.is_multimarket)
      } catch {
        // On error, fall back to legacy API
        if (!cancelled) setMultimarketInCoreResponses(false)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    checkMultimarket()
    return () => {
      cancelled = true
    }
  }, [surveyId, projectLoading, isMultiMarket])

  if (projectLoading) {
    return { isLoading: true, useNewApi: null }
  }

  if (!isMultiMarket) {
    return { isLoading: false, useNewApi: true }
  }

  return {
    isLoading,
    useNewApi: isLoading ? null : !!multimarketInCoreResponses
  }
}
